import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, FileSpreadsheet, Loader2, AlertCircle, CheckCircle2, LogOut, Shield, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformImportBookings() {
  const { user, logout } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [restaurantId, setRestaurantId] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/platform/restaurants`);
        setRestaurants(data.restaurants || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load restaurants');
      } finally {
        setLoadingList(false);
      }
    })();
  }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setResult(null);
  };

  const upload = async () => {
    if (!restaurantId) {
      toast.error('Select which restaurant this CSV is for');
      return;
    }
    if (!file) {
      toast.error('Choose a CSV file first');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please use a .csv export (save as CSV from Excel if needed)');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API}/platform/restaurants/${restaurantId}/import-csv`,
        formData
      );
      setResult(data);
      toast.success(`Imported ${data.imported} booking${data.imported === 1 ? '' : 's'}`);
      if (data.error_count > 0) {
        toast.message(`${data.error_count} row note(s) — see below`, { duration: 6000 });
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : err.response?.data?.detail?.[0]?.msg || 'Import failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const selectedName = restaurants.find((r) => r.id === restaurantId)?.name;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold">Platform</h1>
              <p className="text-xs text-muted-foreground">Signed in as {user?.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/platform/dashboard" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Dashboard
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="gap-2" data-testid="platform-import-logout">
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6" data-testid="platform-import-bookings">
        <div>
          <h2 className="font-heading text-2xl font-bold">Import bookings (CSV)</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            For migrating a venue (e.g. Kolandar or Koalendar “upcoming bookings” CSV) into a tenant. Choose the
            restaurant, then upload the file.
            Confirmation emails are not sent; scheduled reminder emails apply to future bookings like any other row.
          </p>
        </div>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSpreadsheet className="h-5 w-5" />
              Target restaurant
            </CardTitle>
            <CardDescription>Required — imports always attach to one venue.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Restaurant</Label>
              {loadingList ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <Select value={restaurantId} onValueChange={setRestaurantId}>
                  <SelectTrigger data-testid="platform-import-restaurant">
                    <SelectValue placeholder="Select restaurant…" />
                  </SelectTrigger>
                  <SelectContent>
                    {restaurants.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} {r.status === 'suspended' ? '(suspended)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">CSV file</CardTitle>
            <CardDescription>
              <strong>Kolandar</strong> or <strong>Koalendar</strong> export as <strong>CSV</strong>.
              Koalendar files (they include a <strong>Meeting Date</strong> column) use <strong>month/day</strong> in that
              field, including ambiguous values like 05/12 = 12 May. Other exports default to <strong>day/month</strong>
              when ambiguous. ISO <code className="text-xs">YYYY-MM-DD</code> is always recognised. Extra columns are
              ignored. If a header contains a comma, use a proper CSV export with
              quoted fields (e.g. Excel “CSV UTF-8”).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">Choose file</Label>
              <input
                id="csv-file"
                type="file"
                accept=".csv,text/csv"
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-sm file:font-medium"
                onChange={handleFile}
                data-testid="platform-import-csv-input"
              />
            </div>
            <Button onClick={upload} disabled={loading || !file || !restaurantId} className="gap-2" data-testid="platform-import-csv-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import into {selectedName || '…'}
            </Button>
          </CardContent>
        </Card>

        {result && (
          <Card className="border-border/50 border-emerald-200 bg-emerald-50/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
                <CheckCircle2 className="h-5 w-5" />
                Result
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <strong>{result.imported}</strong> bookings imported for <strong>{selectedName}</strong>.
              </p>
              {result.error_count > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50/80 p-3 text-amber-950">
                  <p className="flex items-center gap-2 font-medium">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {result.error_count} message(s)
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-1 text-xs max-h-48 overflow-y-auto">
                    {(result.errors || []).map((e, i) => (
                      <li key={i}>
                        {e.row != null ? `Row ${e.row}: ` : ''}
                        {e.message || JSON.stringify(e)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
